import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileSaveIcon from '../../../../../assets/svg/file_save.svg?react';
import styles from './DocumentListVault.module.scss';
import { v4 as uuidv4 } from 'uuid';
import { Document, DocumentListVaultProps } from './types';
import FileTypeDownloadModal from '../../../../common/file-type-download-modal/FileTypeDownloadModal';
import { useFileTypeDownload } from '../../../../common/file-type-download-modal/useFileTypeDownload';
import { ProgressOverlay } from '../../../../common/progress-overlay/ProgressOverlay';
import { DocumentsEditButton } from '../../../../common/document-edit-button/DocumentsEditButton';
import MessageModal from '../../../../common/message-modal/MessageModal';
import { useDeleteMessageModal } from '../../hooks/useDeleteMessageModal';
import { useLocation } from 'react-router-dom';
import ContractEditIcon from '../../../../../assets/svg/menu/contract-edit.svg?react';
import BrushIcon from '../../../../../assets/svg/brush.svg?react';
import { useDocumentSigning } from '../../hooks/useDocumentSigning';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import { DocumentsListRow } from './DocumentsListRow';
import { DocumentsListRowCompact } from './DocumentsListRowCompact';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectUserRoleByGroupName } from '../../../../slices/user/userSlice';
import { GROUPS } from '../../../../common/constants/groups';
import { useAssignProfilePersonModal } from '../../../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { AssignProfilePersonModal } from '../../../../common/assign-profile-person-modal/AssignProfilePersonModal';

import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import { selectAllEmployees } from '../../../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../../../slices/customers/customersSlice';
import { selectBusinessProfileContacts } from '../../../../slices/business-profile/businessProfileSlice';
import { ROUTES } from '../../../../common/constants/routes';
import {
  showOverlay,
  hideOverlay,
} from '../../../../slices/show-progress-overlay/showProgressOverlay';
import InfoIcon from '@mui/icons-material/Info';
import { Helpers } from '../../../../common/helpers/helpers';
import { NWClient } from '../../../../client/NWClient';
import { DocumentHtmlDataModel } from '../../../../models/document.html.data.model';
import { toast } from 'react-toastify';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';
import { useErrorMessage } from '../../../../common/hooks/useErrorMessage';

const DocumentListVault = ({ documents, cardView, compact, embedded }: DocumentListVaultProps) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const userIsEmployee = Boolean(
    useAppSelector(selectUserRoleByGroupName(GROUPS.COMPANY_EMPLOYEE))
  );
  const location = useLocation();
  const path = location.pathname;
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const dispatch = useAppDispatch();
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const { redirectToDocumenso } = useDocumensoRedirect();
  const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return (businessContacts?.length ?? 0) > 0;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return suppliers?.length > 0;
    }
  };

  const disAllowedExtensions = ['jpg', 'jpeg', 'heic', 'png'];
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    documentToDelete,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
  } = useFileTypeDownload();
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);
  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning(cardView);
  const { patchDocumentWithProfilePerson, setAssignee, assignee } = useAssignProfilePersonModal(
    documentToSign?.category,
    documentToSign
  );

  const renderTableHeader = () => (
    <TableHead>
      <TableRow className={styles.header_row}>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.fileName')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.profile')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.workflow')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.date')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('labels.endRenewalDate')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.fileType')}
          </Typography>
        </TableCell>
        <TableCell className={styles.header_cell}>
          <Typography variant='body2' className={styles.header_text}>
            {t('headers.signed')}
          </Typography>
        </TableCell>
        <TableCell className={`${styles.header_cell} ${styles.actions_header}`}>
          <Typography variant='body2' className={styles.header_text}>
            {t('documentsTable.actions')}
          </Typography>
        </TableCell>
      </TableRow>
    </TableHead>
  );

  const renderActionButtons = (doc: Document) => (
    <Box className={styles.actions_container}>
      {doc?.is_flattened && (doc?.signing_doc_id === null || doc?.signing_doc_id === undefined) && (
        <Tooltip title={t('messages.flattenedPDFMessage')} arrow placement='top'>
          <InfoIcon
            color='primary'
            fontSize='small'
            style={{ marginRight: '8px' }}
            className={'info-icon'}
          />
        </Tooltip>
      )}
      {/*!disAllowedExtensions.some((ext) => (doc?.name ?? '').toLowerCase().endsWith(`.${ext}`)) &&
        (doc?.signing_doc_id === null || doc?.signing_doc_id === undefined) && !doc?.is_flattened &&*/}
      {doc && 'contract_generation' in doc && (
        <Tooltip
          title={userIsEmployee ? t('messages.contactAdministrator') : t('buttons.edit')}
          placement='top'
          arrow
        >
          <IconButton
            id='documentVaultDocumentListVaultEditIconBtn'
            size='small'
            disabled={userIsEmployee}
            onClick={
              userIsEmployee
                ? undefined
                : () => navigateToDocumentPageTab(doc, 0, false, false, true)
            }
            className={styles.action_button}
          >
            <BrushIcon id='documentVaultDocumentListVaultEditIconBtnBrushIcon' />
          </IconButton>
        </Tooltip>
      )}
      {!disAllowedExtensions.some((ext) => doc?.du_filetype?.includes(ext)) &&
        !doc?.is_flattened &&
        !doc?.signing_doc_id && (
          <Tooltip
            title={
              userIsEmployee
                ? t('messages.contactAdministrator')
                : doc &&
                    'du_filetype' in doc &&
                    doc?.du_filetype &&
                    doc?.du_filetype.length > 0 &&
                    doc?.du_filetype[0] !== 'pdf' &&
                    !('contract_generation' in doc)
                  ? t('messages.convertToPDFMessage')
                  : doc?.signing_doc_id !== null &&
                      doc?.signing_doc_id !== undefined &&
                      doc.status === 'COMPLETED'
                    ? t('buttons.signedDocument')
                    : doc?.signing_doc_id !== null &&
                        doc?.signing_doc_id !== undefined &&
                        doc.status === 'PENDING'
                      ? t('buttons.documentInSigning')
                      : t('buttons.sign')
            }
            placement='top'
            arrow
          >
            <IconButton
              id='documentVaultDocumentListVaultSignIconBtn'
              size='small'
              disabled={userIsEmployee}
              onClick={
                userIsEmployee ||
                (doc &&
                  'du_filetype' in doc &&
                  doc?.du_filetype &&
                  doc?.du_filetype.length > 0 &&
                  doc?.du_filetype[0] !== 'pdf' &&
                  !('contract_generation' in doc))
                  ? undefined
                  : () => {
                      if (doc?.signing_doc_id !== null && doc?.signing_doc_id !== undefined) {
                        redirectToDocumenso(
                          `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
                        );
                      } else {
                        if (
                          (doc.customers?.length ?? 0) === 0 &&
                          (doc.employees?.length ?? 0) === 0 &&
                          //(doc.suppliers?.length ?? 0) === 0 &&
                          doc.category !== 'business' &&
                          doc.category !== 'suppliers' &&
                          checkProfilePersonsAvailability(doc.category ?? '')
                        ) {
                          setDocumentToSign(doc);
                          setOpenAssignPersonDialog(true);
                        } else {
                          dispatch(showOverlay());
                          const tab = window.open(
                            `${window.location.origin}${ROUTES.PROCESSING}`,
                            '_blank'
                          );
                          setDocumentToSign(doc);
                          /*if (
                            doc &&
                            doc.document_upload_html &&
                            doc.document_upload_html.length > 0
                          ) {
                            handleSignLatestVersion(
                              doc,
                              undefined,
                              undefined,
                              'contract_generation' in doc,
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              //@ts-ignore
                              tab
                            );
                          } else {*/
                          // @ts-expect-error: there can be an error
                          handleSignOriginalDocument(doc, undefined, true, tab);
                          /*}*/
                        }
                      }
                    }
              }
              className={styles.action_button}
            >
              <ContractEditIcon id='documentVaultDocumentListVaultSignIconBtnContractEditIcon' />
            </IconButton>
          </Tooltip>
        )}
      <Tooltip title={t('buttons.download')} placement='top' arrow>
        <IconButton
          id='documentVaultDocumentListVaultDownloadIconBtn'
          size='small'
          onClick={() => {
            if (doc.signing_doc_id /*&& doc.status === 'COMPLETED'*/) {
              redirectToDocumenso(
                `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${path}&activeTab=${doc.category}`
              ).catch();
            } else {
              if ('contract_generation' in doc) {
                setDocumentForDownloading(doc);
                setFileNameForDownloading(doc.name ?? '');
                setFileTypeModalOpen(true);
              } else {
                /*if ((doc.document_upload_html?.length ?? 0) > 0) {
                  setDocumentForDownloading(doc);
                  setFileTypeModalOpen(true);
                } else {*/
                downloadDocumentUploadItem(doc.id, doc.name ?? '');
                /*}*/
              }
            }
          }}
          className={styles.action_button}
        >
          <FileSaveIcon id='documentVaultDocumentListVaultDownloadIconBtnFileSaveIcon' />
        </IconButton>
      </Tooltip>
      {/*{(doc?.signing_doc_id === null || doc?.signing_doc_id === undefined) && (*/}
      <DocumentsEditButton
        document={doc}
        isImage={disAllowedExtensions.some((ext) => doc?.du_filetype?.includes(ext))}
        getDocumentSummaryFn={
          doc?.signing_doc_id ? undefined : () => navigateToDocumentPageTab(doc, 1, false, true)
        }
        createTemplateFn={
          undefined //let it be undefined until the proper fix
          /*!doc.contract_generation && !userIsEmployee && !doc.is_flattened
              ? () => navigateToDocumentPageTab(doc, 0, true)
              : undefined*/
        }
        duplicateDocumentFn={doc?.signing_doc_id ? undefined : true}
        deleteDocumentFn={() => {
          setDocumentToDelete(doc);
          setDeleteMessageModalOpen(true);
        }}
        documentVersionsFn={undefined /*() => navigateToDocumentPageTab(doc, 1)*/}
        documentLabelsFn={doc?.signing_doc_id ? undefined : () => navigateToDocumentPageTab(doc, 1)}
      />
      {/*)}*/}
    </Box>
  );

  return (
    <>
      <TableContainer
        component={Paper}
        className={`${styles.table_container}${compact ? ` ${styles.table_container_compact}` : ''}${
          embedded ? ` ${styles.table_container_embedded}` : ''
        }`}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          // The embedded table takes its max height from `.table_container_embedded`
          // (desktop only: on tablet / mobile the aside sits above it and the page scrolls).
          ...(embedded ? {} : { maxHeight: 'calc(100vh - 200px)' }),
          overflow: 'auto',
        }}
      >
        <Table
          stickyHeader={!compact}
          className={compact ? styles.compact_table : undefined}
          aria-label={compact ? t('headers.documentVault') : undefined}
        >
          {!compact && renderTableHeader()}
          <TableBody>
            {documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={compact ? 3 : 8}>
                  <p className={styles.empty_message}>{t('messages.emptyDocumentsMessage')}</p>
                </TableCell>
              </TableRow>
            )}
            {documents.map((doc) =>
              compact ? (
                <DocumentsListRowCompact
                  key={`${doc.id}-${uuidv4()}`}
                  doc={doc}
                  renderActionButtons={renderActionButtons}
                />
              ) : (
                <DocumentsListRow
                  key={`${doc.id}-${uuidv4()}`}
                  doc={doc}
                  renderActionButtons={renderActionButtons}
                />
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={(value) =>
            setFileTypeForDownloading(value as React.SetStateAction<'pdf' | 'doc'>)
          }
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            if (fileTypeForDownloading === 'pdf') {
              setPdfSaveDialogOpen(true);
              setFileTypeModalOpen(false);
            } else {
              handleDownloadContract(documentForDownloading.id);
            }
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name ?? '')
          }
          handleDownloadHTML={
            documentForDownloading?.du_filetype &&
            documentForDownloading?.du_filetype.includes('pdf')
              ? () => {
                  setPdfSaveDialogOpen(true);
                  setFileTypeModalOpen(false);
                }
              : () => {
                  const documentName = documentForDownloading.name ?? '';
                  handleDownloadHTML(
                    (documentForDownloading?.document_upload_html?.length ?? 0) > 0
                      ? Math.max(...(documentForDownloading.document_upload_html ?? []))
                      : 0,
                    documentName.endsWith('.pdf') ||
                      documentName.endsWith('.docx') ||
                      documentName.endsWith('.doc')
                      ? documentName.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
                      : documentName,
                    documentForDownloading.du_filetype?.[0] ?? ''
                  );
                }
          }
        />
      )}
      {pdfSaveDialogOpen && documentForDownloading && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            if (documentForDownloading?.html) {
              Helpers.convertHtmlToPdfAndDownload(
                documentForDownloading?.html,
                documentForDownloading?.name?.replace('.pdf', '')
              );
              setPdfSaveDialogOpen(false);
            } else {
              dispatch(showOverlay());
              NWClient.get<DocumentHtmlDataModel>(
                'document-upload-html-data',
                Math.max(...(documentForDownloading.document_upload_html ?? []))
              )
                .then((res) => {
                  Helpers.convertHtmlToPdfAndDownload(
                    res.html,
                    documentForDownloading?.name?.replace('.pdf', '')
                  );
                  setPdfSaveDialogOpen(false);
                  setDocumentForDownloading(null as unknown as Document);
                })
                .catch((error) => {
                  toast.error(getErrorMessage(error));
                })
                .finally(() => {
                  dispatch(hideOverlay());
                  setPdfSaveDialogOpen(false);
                });
            }
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          onDismiss={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          document={documentToDelete ?? undefined}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {/*signingDialogOpen && (
        <MessageModal
          open={signingDialogOpen}
          modalMessage={t('messages.signingVersionMessage')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonFn={() => handleSignLatestVersion(documentToSign)}
          actionButtonAdditionalText={t('buttons.signOriginalDocument')}
          actionButtonAdditionalFn={() => handleSignOriginalDocument(documentToSign)}
          goBackButtonText={t('buttons.goBack')}
          document={documentToSign}
          onClose={() => {
            setDocumentToSign(null);
            setSigningDialogOpen(false);
          }}
        />
      )*/}
      {openAssignPersonDialog && documentToSign && (
        <AssignProfilePersonModal
          open={openAssignPersonDialog}
          onClose={() => {
            setTimeout(() => {
              setOpenAssignPersonDialog(false);
              // @ts-expect-error: some error
              setDocumentToSign(null);
              setAssignee('');
            }, 100);
          }}
          modalTitle={t('headers.startSigning')}
          category={documentToSign?.category ?? ''}
          document={documentToSign}
          goBackButtonText={t('buttons.goBack')}
          modalMessage={t('messages.signingRecommendations')}
          assignee={assignee}
          setAssignee={setAssignee}
          secondaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            setOpenAssignPersonDialog(false);
            if (assignee && assignee !== 'noAssignee') {
              if (documentToSign?.category === 'suppliers') {
                handleSignOriginalDocument(
                  documentToSign,
                  Number(assignee),
                  true,
                  // @ts-expect-error: there can be an error
                  tab
                );
                setAssignee('');
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => {
                    handleSignOriginalDocument(
                      res,
                      undefined,
                      'contract_generation' in documentToSign,
                      // @ts-expect-error: there can be an error
                      tab
                    );
                    setAssignee('');
                  })
                  .catch(() => {
                    handleSignOriginalDocument(
                      documentToSign,
                      undefined,
                      true,
                      // @ts-expect-error: there can be an error
                      tab
                    );
                    setAssignee('');
                  });
              }
            } else {
              // @ts-expect-error: there can be an error
              handleSignOriginalDocument(documentToSign, undefined, true, tab);
            }
          }}
          primaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            setOpenAssignPersonDialog(false);
            if (assignee && assignee !== 'noAssignee') {
              if (documentToSign?.category === 'suppliers') {
                handleSignLatestVersion(
                  documentToSign,
                  undefined,
                  Number(assignee),
                  'contract_generation' in documentToSign,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  tab
                );
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => {
                    handleSignLatestVersion(
                      res,
                      undefined,
                      undefined,
                      'contract_generation' in documentToSign,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      tab
                    );
                    setAssignee('');
                  })
                  .catch(() => {
                    handleSignLatestVersion(
                      documentToSign,
                      undefined,
                      undefined,
                      'contract_generation' in documentToSign,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      tab
                    );
                    setAssignee('');
                  });
              }
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              handleSignLatestVersion(documentToSign, undefined, undefined, true, tab);
              setAssignee('');
            }
          }}
        />
      )}
      {documentDeletePendingStatus && <ProgressOverlay />}
    </>
  );
};

export default DocumentListVault;
